import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Replay } from '@styled-icons/material/Replay'
import { useAtom } from 'jotai'
import {
  controlsVisibilityLockAtom,
  ownsNFTAtom,
  videoElementAtom,
  wControlsVisibilityAtom,
} from './atoms'
import TokengateButton from './TokengateButton'
import { VideoData } from '../../../interfaces'
import PreviewTime from '../../preview/PreviewTime'

//* ============================ React component ===============================

const TokengateOverlay = ({
  videoMetadata,
  scheduledTime,
}: {
  videoMetadata: VideoData
  scheduledTime: Date
}): JSX.Element => {
  const [videoElement] = useAtom(videoElementAtom)
  const [isLive, setIsLive] = useState(scheduledTime <= new Date())
  const [withinLastSecond, setWithinLastSecond] = useState(false)
  const [, setControlsVisibilityLocked] = useAtom(controlsVisibilityLockAtom)
  const [, setControlsVisibility] = useAtom(wControlsVisibilityAtom)

  const [ownsNFT] = useAtom(ownsNFTAtom)

  useEffect(() => {
    if (!videoElement) {
      return
    }

    videoElement.addEventListener('timeupdate', () => {
      const withinLastSecond = videoElement.currentTime > videoElement.duration - 1

      if (withinLastSecond) {
        setWithinLastSecond(true)

        // Hide the controls and lock them.
        setControlsVisibilityLocked(false)
        setControlsVisibility(false)
        document.exitPointerLock()
      } else {
        setWithinLastSecond(false)

        // Unlock the controls again.
        //! This creates a massive bug as it is set to false again all the time.
        // setControlsVisibilityLocked(false)setControlsVisibilityLocked(false)
      }
    })
  }, [videoElement, setControlsVisibility])

  return (
    <Wrapper visible={withinLastSecond}>
      <Center>
        <Column>
          <Text>
            {videoMetadata.gated
              ? !isLive
                ? 'Full video premiers...'
                : ownsNFT
                ? `Done watching?`
                : `To watch the full video, you must own the NFT.`
              : !isLive
              ? 'Full video premiers...'
              : ownsNFT
              ? `Done watching?`
              : `Add this video to your collection.`}
          </Text>
          {!isLive && (
            <PreviewTime scheduledTime={scheduledTime} setIsLive={setIsLive} isLive={isLive} />
          )}
          <TokengateButton videoMetadata={videoMetadata} scheduledTime={scheduledTime} />
          <ReplayButtonRow
            onClick={() => {
              videoElement!.currentTime = 0
              videoElement!.play()
            }}
          >
            <ReplayButtonIcon />
            <ReplayButtonText>Replay</ReplayButtonText>
          </ReplayButtonRow>
        </Column>
      </Center>
    </Wrapper>
  )
}

export default TokengateOverlay

//* ============================ Styled Components =============================

const Wrapper = styled.div<{ visible: boolean }>`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: all;
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  background: rgba(0, 0, 0, 0.8);
  transition: opacity 1000ms ease-in;
`

const ReplayButtonRow = styled.div`
  pointer-events: all;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  padding: 5px 5px 5px 5px;

  @media (min-width: 43em) {
    gap: 10px;
  }

  @media (min-width: 62em) {
    gap: 5px;
  }
`

const ReplayButtonText = styled.div`
  font-size: 14px;
  color: white;

  @media (min-width: 43em) {
    font-size: 18px;
  }
`

const ReplayButtonIcon = styled(Replay)`
  width: 16px;
  height: 16px;
  pointer-events: auto;
  cursor: pointer;

  @media (min-width: 43em) {
    width: 20px;
    height: 20px;
  }
`

const Text = styled.div`
  color: white;
  font-size: 16px;
  text-align: center;

  @media (min-width: 43em) {
    font-size: 22px;
  }

  @media (min-width: 62em) {
    font-size: 24px;
  }
`

const Center = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;

  @media (min-width: 43em) {
    gap: 15px;
  }

  @media (min-width: 62em) {
    gap: 15px;
  }
`
